import { addDays, format } from "date-fns";
import { Locale } from "date-fns/locale";

import { AttendanceOverviewEntryResponse, AttendanceOverviewRowResponse, PurposeType } from "@/api";
import { WEEKDAY_COUNT } from "@/common/components/appointments/shared/appointmentConstants";
import { dateOnlyKey } from "@/common/utils/dateOnly";

/** Empty string = "Alle …", the unset value of every Anwesenheitsliste filter. */
export const ALL_FILTER_VALUE = "";

/** The Termine of a row — the only entries the Termine and Orte filters look at. */
export function getAppointmentEntries(row: AttendanceOverviewRowResponse): AttendanceOverviewEntryResponse[] {
  return row.entries.filter((entry) => entry.purposeType === PurposeType.Appointment);
}

/** The Anwesenheiten of a row (presences and absences alike) — the only entries the Anwesenheiten filter looks at. */
export function getAttendanceEntries(row: AttendanceOverviewRowResponse): AttendanceOverviewEntryResponse[] {
  return row.entries.filter((entry) => entry.purposeType === PurposeType.Attendance);
}

/**
 * Whether a row survives the Termine and Orte filters. Both values must be met by one and the same
 * Termin, so a row holding a matching Zweck and a matching Ort on two different Termine is out.
 * Absences and plain working hours are never Termine, so a row made up of only those drops out as
 * soon as either filter is set. A Termin without an Ort matches no selected Ort — the Ort is
 * optional on an appointment, and inventing a match for a missing value would widen the filter.
 */
export function matchesAppointmentFilters(
  row: AttendanceOverviewRowResponse,
  appointmentPurposeKey: string,
  location: string
): boolean {
  if (appointmentPurposeKey === ALL_FILTER_VALUE && location === ALL_FILTER_VALUE) {
    return true;
  }
  return getAppointmentEntries(row).some(
    (entry) =>
      (appointmentPurposeKey === ALL_FILTER_VALUE || entry.purposeKey === appointmentPurposeKey) &&
      (location === ALL_FILTER_VALUE || entry.location === location)
  );
}

/**
 * Whether a row survives the Anwesenheiten filter: it needs at least one Anwesenheit of the selected
 * Zweck somewhere in the week. The Anwesenheit and a Termin matched by the other filters are separate
 * entries, so they need not fall on the same day — the filters are combined per row, not per day.
 */
export function matchesAttendanceFilter(row: AttendanceOverviewRowResponse, attendancePurposeKey: string): boolean {
  if (attendancePurposeKey === ALL_FILTER_VALUE) {
    return true;
  }
  return getAttendanceEntries(row).some((entry) => entry.purposeKey === attendancePurposeKey);
}

/** The Mon–Fri days of the week starting at the given Monday (local dates). */
export function getWeekDays(weekStart: Date): Date[] {
  return Array.from({ length: WEEKDAY_COUNT }, (_, index) => addDays(weekStart, index));
}

/** Day key for a local Date (the week's day columns). */
export function localDayKey(day: Date): string {
  return format(day, "yyyy-MM-dd");
}

export function groupEntriesByDay(row: AttendanceOverviewRowResponse): Map<string, AttendanceOverviewEntryResponse[]> {
  const groups = new Map<string, AttendanceOverviewEntryResponse[]>();
  for (const entry of row.entries) {
    const key = dateOnlyKey(entry.date);
    const group = groups.get(key);
    if (group) {
      group.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  }
  return groups;
}

function formatShortTime(date: Date, locale: Locale | undefined): string {
  return format(date, date.getMinutes() === 0 ? "H" : "H:mm", { locale });
}

/** Compact local time range, e.g. "8–16" or "7:30–15". */
export function formatTimeRange(start: Date, end: Date, locale: Locale | undefined): string {
  return `${formatShortTime(start, locale)}–${formatShortTime(end, locale)}`;
}

function isMidnight(date: Date): boolean {
  return date.getHours() === 0 && date.getMinutes() === 0;
}

/**
 * Whether an entry covers whole days and therefore has no time range to show — formatting one renders
 * as "0–0". All-day appointments are stored from local midnight of the first day to local midnight
 * after the last one (Appointment.AdjustAllDayDates on the backend, pinned by
 * GetAttendanceOverviewQueryHandlerTests), which no timed entry from the picker can look like: the
 * earliest of those is 00:00–00:30. "Local" is the browser timezone, the same one the overview query
 * sends as its origin timezone.
 */
export function isAllDayRange(start: Date, end: Date): boolean {
  return isMidnight(start) && isMidnight(end);
}

/**
 * Attendance tag label: "Anwesenheit Büro" → "Büro", "Anwesenheit Mobil" → "Mobil". Derived from the
 * purpose label rather than a fixed set of flags, so every present-attendance purpose — including one
 * FSW adds later — labels itself and office attendance never reads the same as home office.
 */
export function formatAttendanceLabel(purposeLabel: string): string {
  return purposeLabel.replace(/^Anwesenheit\s+/i, "");
}

/**
 * Formats an API DateOnly value as d.M. — used for the "erreichbar ab" date, which the backend
 * computes (it owns the public-holiday calendar). DateOnly values deserialize as UTC-midnight Dates,
 * so the day is rebuilt from the UTC parts; local formatting would shift it to the previous day in
 * behind-UTC (UTC−) timezones.
 */
export function formatDayMonth(date: Date, locale: Locale | undefined): string {
  return format(new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()), "d.M.", { locale });
}

const MAX_NOTE_LENGTH = 40;

/** Truncates an absence note for the day cell; the full text is shown via tooltip. */
export function truncateNote(note: string): string {
  return note.length <= MAX_NOTE_LENGTH ? note : `${note.slice(0, MAX_NOTE_LENGTH).trimEnd()}…`;
}
